/**
 * Google Analytics 4 (GA4) integration for Ministry of Jesus.
 *
 * Design goals:
 *  - Production only: Preview (*.preview.emergentagent.com) and localhost are
 *    always skipped to avoid polluting analytics with development traffic.
 *  - GDPR compliant: gtag.js is NEVER loaded until the visitor accepts the
 *    cookie banner. Consent choice is persisted in localStorage.
 *  - SPA-aware: exposes trackPageView() so route changes can be reported to GA.
 */

export const GA_MEASUREMENT_ID = 'G-QWXRD9PV5Y';
export const CONSENT_STORAGE_KEY = 'moj-cookie-consent';
export const CONSENT_ACCEPTED = 'accepted';
export const CONSENT_DECLINED = 'declined';

const PREVIEW_HOST_FRAGMENT = 'preview.emergentagent.com';

let scriptInjected = false;

/**
 * Returns true only when the page is running on a real production hostname
 * (not on the Emergent preview subdomain and not on localhost).
 */
export function isProductionEnvironment() {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname || '';
  if (!host) return false;
  if (host === 'localhost' || host === '127.0.0.1') return false;
  if (host.endsWith(PREVIEW_HOST_FRAGMENT)) return false;
  return true;
}

/** Read the persisted consent choice, if any. */
export function getStoredConsent() {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(CONSENT_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Persist the visitor's consent choice. */
export function setStoredConsent(value) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, value);
  } catch {
    /* ignore quota / privacy-mode errors */
  }
}

/**
 * Inject the gtag.js script tag and initialise the GA4 property.
 * Safe to call multiple times — the script is only injected once.
 */
export function initAnalytics() {
  if (typeof window === 'undefined') return;
  if (!isProductionEnvironment()) return;
  if (getStoredConsent() !== CONSENT_ACCEPTED) return;
  if (scriptInjected || window.gtag) return;

  scriptInjected = true;

  // Standard GA4 bootstrap.
  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line func-names
  window.gtag = function () {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  // We fire the initial page_view ourselves after route changes,
  // so disable the automatic one to avoid double counting.
  window.gtag('config', GA_MEASUREMENT_ID, {
    send_page_view: false,
    anonymize_ip: true,
  });

  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(s);
}

/**
 * Report an SPA page view to GA. No-op when analytics isn't active
 * (preview environment, consent declined, or script not yet loaded).
 */
export function trackPageView(path) {
  if (typeof window === 'undefined') return;
  if (!isProductionEnvironment()) return;
  if (getStoredConsent() !== CONSENT_ACCEPTED) return;
  if (typeof window.gtag !== 'function') return;

  const pagePath = path || window.location.pathname + window.location.search;
  window.gtag('event', 'page_view', {
    page_path: pagePath,
    page_location: window.location.href,
    page_title: document.title,
  });
}
